import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useOutletContext, useSearchParams } from 'react-router-dom'

import { useLayout } from '@app/routing/LayoutContext'

import { ChartResizeHandle } from '@pages/Trade/Markets/Charts/ChartResizeHandle'
import {
  TV_MIN_CHART_HEIGHT_PX,
  TradingViewChart,
} from '@pages/Trade/Markets/Charts/TradingViewChart'
import {
  readStoredChartTimeframe,
  readStoredChartTimezone,
  writeStoredChartTimeframe,
  writeStoredChartTimezone,
} from '@pages/Trade/Markets/Charts/utils/utils'
import { useDxtradeOpenPositions } from '@pages/Trade/Portfolio/hooks/useDxtradeOpenPositions'
import { WidgetContainer } from '@pages/Trade/Widget/WidgetContainer'
import { ChartToolbar } from '@pages/Trade/components/ChartToolbar'
import { UseTradeLoginReturn } from '@pages/Trade/hooks/useTradeLogin'

import { useModal } from '@context/ModalsContext'

import { SearchField } from '@ui/SearchField/SearchField'
import { Title1 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Divider } from '@ui/components/Divider'
import { ChartBarIcon } from '@ui/icons/ChartBar.icon'
import { SearchIcon } from '@ui/icons/Search.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { AccountGroupType } from '@model/AccountGroupType'

import { safeLocalStorage } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import {
  getMetricsStoreSnapshot,
  subscribeToMetricsStore,
} from '../Portfolio/hooks/useDxtradeAccountWebSocket'
import { WIDGET_TYPE } from '../utils/constants'
import Instruments from './Instruments/Instruments'
import { useDxtradeWatchlists } from './Instruments/hooks/useDxtradeWatchlists'
import { useFavoritesWatchlist } from './Instruments/hooks/useFavoritesWatchlist'
import { DepositModal } from './Instruments/modals/DepositModal'
import { useMarketsWidgetController } from './hooks/useMarketsWidgetController'

import styles from './Markets.page.module.scss'

type ChartLayoutState = {
  height: number
  collapsed: boolean
  fullscreen: boolean
}
/** persisted chart height key. Versioned so we can invalidate stale values on layout changes. */
const CHART_LAYOUT_STORAGE_KEY = 'markets.chartLayout.v1'
/** min usable instruments list height. Below this the list becomes useless. */
const INSTRUMENTS_MIN_HEIGHT_PX = 160
/** default fallback chart height when nothing is persisted (~49.5vh on a 1080p display). */
const DEFAULT_CHART_HEIGHT_PX = 320
const CHART_COLLAPSE_THRESHOLD_PX = TV_MIN_CHART_HEIGHT_PX + 24
const readStoredChartLayout = (): Partial<ChartLayoutState> => {
  if (typeof window === 'undefined') return {}

  try {
    const raw = safeLocalStorage.getItem(CHART_LAYOUT_STORAGE_KEY)

    if (!raw) return {}

    const parsed = JSON.parse(raw)

    return {
      height: Number.isFinite(parsed.height) && parsed.height > 0 ? parsed.height : undefined,
      collapsed: typeof parsed.collapsed === 'boolean' ? parsed.collapsed : undefined,
      fullscreen: typeof parsed.fullscreen === 'boolean' ? parsed.fullscreen : undefined,
    }
  } catch {
    return {}
  }
}

const writeStoredChartLayout = (state: ChartLayoutState) => {
  if (typeof window === 'undefined') return

  try {
    safeLocalStorage.setItem(
      CHART_LAYOUT_STORAGE_KEY,
      JSON.stringify({
        height: Math.round(state.height),
        collapsed: state.collapsed,
        fullscreen: state.fullscreen,
      })
    )
  } catch {
    console.debug('Cannot setItem in LocalStorage.')
  }
}

interface IncomingRedirectState {
  symbol?: string
  isInstrumentRedirect?: boolean
}

export const useIncomingInstrumentRedirect = () => {
  const location = useLocation()

  const [searchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useState('')

  const getIncomingSymbol = (
    state: IncomingRedirectState,
    searchParams: URLSearchParams
  ): string | undefined => {
    return state?.symbol || searchParams.get('symbol') || undefined
  }

  const [rowSelectedInstrumentSymbol, setRowSelectedInstrumentSymbol] = useState<
    string | undefined
  >(() => getIncomingSymbol(location.state, searchParams))

  useEffect(() => {
    const state = location.state as IncomingRedirectState | null
    const symbol = state?.symbol ?? searchParams.get('symbol') ?? undefined

    if (state?.isInstrumentRedirect !== true || !symbol) return

    setRowSelectedInstrumentSymbol(symbol)
    setSearchValue(symbol)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  return {
    searchValue,
    rowSelectedInstrumentSymbol,
    setSearchValue,
    setRowSelectedInstrumentSymbol,
  }
}

export const MarketsPage = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const { loading, isLoggedIn, selectedAccount } = useOutletContext<UseTradeLoginReturn>()
  const apiClient = useApiClient(ClientApiClient)
  const selectedAccountLogin = selectedAccount?.platformOverview?.login ?? ''

  const isMobile = useWindowResize()
  const { layout } = useLayout()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [defaultSymbol, setDefaultSymbol] = useState<string | undefined>()
  const [selectedInstrumentPriceIncrement, setSelectedInstrumentPriceIncrement] = useState<
    number | null | undefined
  >()
  const [storedChartLayout] = useState(() => readStoredChartLayout())

  const [isChartCollapsed, setIsChartCollapsed] = useState(storedChartLayout.collapsed ?? false)

  const [isChartFullscreen, setIsChartFullscreen] = useState(storedChartLayout.fullscreen ?? false)
  const [chartHeightPx, setChartHeightPx] = useState<number>(
    () => storedChartLayout.height ?? DEFAULT_CHART_HEIGHT_PX
  )

  const { data: openPositionsData } = useDxtradeOpenPositions(selectedAccountLogin)

  const wsAccountKey = `default:${selectedAccountLogin}`
  const wsBalance = useSyncExternalStore(
    (cb) => subscribeToMetricsStore(wsAccountKey, cb),
    () => getMetricsStoreSnapshot(wsAccountKey)?.balance
  )
  const accountBalance = wsBalance ?? selectedAccount?.platformOverview?.balance ?? 0

  const selectedAccountId = selectedAccount?.id ?? ''
  const accountExternalId = selectedAccount?.externalId
  const { watchlists = [], privateWatchlists } = useDxtradeWatchlists(
    accountExternalId ? { login: accountExternalId, domain: 'default' } : undefined
  )
  const { favoriteSymbols, handleToggleFavorite } = useFavoritesWatchlist(
    accountExternalId,
    privateWatchlists
  )

  const { open: openDepositModal } = useModal(DepositModal, {
    initialIsOpen: location.state?.showReadyToStartTradingModal ?? false,
    accountId: selectedAccountId,
  })

  const isLiveAccount = selectedAccount?.accountGroupType?.id === AccountGroupType.Live
  const instrumentsRef = useRef<HTMLDivElement | null>(null)
  const widgetContainerRef = useRef<HTMLDivElement | null>(null)
  const widgetWasOpenRef = useRef(false)
  const [instrumentsHeight, setInstrumentsHeight] = useState<number | null>(null)
  // Height available for the instruments+chart column. Used to clamp the drag.
  const [wrapperHeight, setWrapperHeight] = useState<number | null>(null)

  const [chartTimeframe, setChartTimeframe] = useState(() => readStoredChartTimeframe())
  const [chartTimezone, setChartTimezone] = useState(() => readStoredChartTimezone())
  const {
    widgetState,
    handleInstrumentAction,
    handleInstrumentSelect,
    handleSideChange,
    handleTabChange,
    handleWidgetClose,
    switchToOrderFromMarket,
  } = useMarketsWidgetController({
    accountBalance,
    isMobile,
    isLiveAccount: isLiveAccount,
    selectedAccountId,
    selectedAccountLogin,
    openDepositModal,
  })

  const {
    searchValue,
    rowSelectedInstrumentSymbol,
    setSearchValue,
    setRowSelectedInstrumentSymbol,
  } = useIncomingInstrumentRedirect()
  useEffect(() => {
    writeStoredChartLayout({
      height: chartHeightPx,
      collapsed: isChartCollapsed,
      fullscreen: isChartFullscreen,
    })
  }, [chartHeightPx, isChartCollapsed, isChartFullscreen])
  useEffect(() => {
    window.addEventListener('trade:close-market-widget', handleWidgetClose)

    return () => {
      window.removeEventListener('trade:close-market-widget', handleWidgetClose)
    }
  }, [handleWidgetClose])

  const handleChartResize = useCallback((nextHeight: number) => {
    setChartHeightPx(nextHeight)
  }, [])

  const handleChartResizeEnd = useCallback((height: number) => {
    if (height <= CHART_COLLAPSE_THRESHOLD_PX) {
      setIsChartCollapsed(true)
      return
    }

    setChartHeightPx(height)
    setIsChartCollapsed(false)
  }, [])
  const handleChartOpen = useCallback(() => {
    setIsChartCollapsed(false)

    setChartHeightPx((prev) =>
      prev <= CHART_COLLAPSE_THRESHOLD_PX ? DEFAULT_CHART_HEIGHT_PX : prev
    )
  }, [])
  const handleChartTradeClick = (side: 'BUY' | 'SELL', chartSymbol: string) => {
    const instrumentForAction = {
      symbol: chartSymbol,
    }

    handleInstrumentAction(instrumentForAction, side === 'BUY' ? 'buy' : 'sell')
  }

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    if (isMobile && widgetState) {
      document.body.dataset.tradeWidgetOpen = 'true'
    } else {
      delete document.body.dataset.tradeWidgetOpen
    }

    return () => {
      delete document.body.dataset.tradeWidgetOpen
    }
  }, [isMobile, widgetState])

  useEffect(() => {
    if (!isMobile && widgetState?.widgetType === 'market') {
      handleWidgetClose()
    }
  }, [handleWidgetClose, isMobile, widgetState?.widgetType])

  useEffect(() => {
    if (!isMobile) {
      widgetWasOpenRef.current = Boolean(widgetState)
      return
    }

    if (widgetState && !widgetWasOpenRef.current && widgetContainerRef.current) {
      requestAnimationFrame(() => {
        const OFFSET = 40

        window.scrollTo({
          top: widgetContainerRef.current!.getBoundingClientRect().top + window.scrollY - OFFSET,
          behavior: 'smooth',
        })
      })
    }

    widgetWasOpenRef.current = Boolean(widgetState)
  }, [isMobile, widgetState])

  useEffect(() => {
    const target = instrumentsRef.current
    if (!target) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]

      if (entry) {
        setInstrumentsHeight(entry.contentRect.bottom)
        setWrapperHeight(entry.contentRect.height)
      }
    })

    observer.observe(target)
    return () => observer.disconnect()
  }, [layout.content])

  // upper bound for the chart-leave at least INSTRUMENTS_MIN_HEIGHT_PX for the list
  // if we haven't measured, yet we fall back to a generous value so we don't over-clamp
  const chartMaxPx = wrapperHeight
    ? Math.max(TV_MIN_CHART_HEIGHT_PX, wrapperHeight - INSTRUMENTS_MIN_HEIGHT_PX)
    : Number.POSITIVE_INFINITY

  // keep chart within bounds if the viewport shrinks below the stored value
  useEffect(() => {
    if (!Number.isFinite(chartMaxPx)) return
    setChartHeightPx((prev) => {
      const clamped = Math.min(chartMaxPx, Math.max(TV_MIN_CHART_HEIGHT_PX, prev))
      return clamped === prev ? prev : clamped
    })
  }, [chartMaxPx])

  if (loading) {
    return <Loading isLoading showLoadingIcon />
  }

  if (isLoggedIn && selectedAccount) {
    const showSearch = !isMobile || isSearchOpen
    const widgetContainerStyle = instrumentsHeight ? { maxHeight: instrumentsHeight } : undefined

    const selectedInstrumentSymbol =
      rowSelectedInstrumentSymbol || widgetState?.symbol || defaultSymbol

    return (
      <div
        className={classNames(styles.pageLayout, {
          [styles.pageLayoutFullscreen]: isChartFullscreen,
        })}
      >
        <div
          className={classNames(styles.instrumentWrapper, {
            [styles.instrumentWrapperCollapsed]: isChartCollapsed,
          })}
          ref={instrumentsRef}
        >
          <div className={styles.instrumentsSection}>
            {isMobile && (
              <div className={styles.mobileHeader}>
                <AnimatePresence initial={false} mode='wait'>
                  {!isSearchOpen && (
                    <Box flex align='center' gap='x8' key='title'>
                      <ChartBarIcon color='text-high-em' />
                      <Title1 medium>{t('Instruments.Markets')}</Title1>
                      <Box ml='auto'>
                        <IconButton.Secondary
                          size='md'
                          icon={SearchIcon}
                          onClick={() => setIsSearchOpen(true)}
                        />
                      </Box>
                    </Box>
                  )}
                  {isSearchOpen && (
                    <motion.div
                      key='search'
                      className={styles.mobileSearchHeader}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -4 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className={styles.mobileSearchFieldWrapper}>
                        <SearchField
                          label={t('Instruments.Search instruments')}
                          value={searchValue}
                          onChange={setSearchValue}
                          canClear
                          autoFocus
                          containerClassName={styles.mobileSearchFieldContainer}
                        />
                      </div>
                      <Button.Ghost
                        size='md'
                        onClick={() => {
                          setSearchValue('')
                          setIsSearchOpen(false)
                        }}
                      >
                        {t('Instruments.Cancel')}
                      </Button.Ghost>
                    </motion.div>
                  )}
                </AnimatePresence>
                <Box pt='x12'>
                  <Divider.Horizontal />
                </Box>
              </div>
            )}
            <Instruments
              account={selectedAccount}
              accountCode={selectedAccountLogin}
              ownerExternalId={accountExternalId ?? undefined}
              watchlists={watchlists}
              privateWatchlists={privateWatchlists}
              favoriteSymbols={favoriteSymbols}
              onToggleFavorite={handleToggleFavorite}
              openPositions={openPositionsData.positions ?? []}
              selectedInstrumentSymbol={selectedInstrumentSymbol}
              onInstrumentSelect={(symbol, priceIncrement) => {
                setRowSelectedInstrumentSymbol(symbol)
                setSelectedInstrumentPriceIncrement(priceIncrement)
                handleInstrumentSelect(symbol)
              }}
              onInstrumentAction={(instrument, action) => {
                setRowSelectedInstrumentSymbol(instrument.symbol ?? undefined)
                handleInstrumentAction(instrument, action)
              }}
              showSearch={showSearch}
              isSearchOpen={isSearchOpen}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              collapsed={isChartCollapsed}
              onToggleCollapse={setIsChartCollapsed}
              isOrderWidgetOpen={widgetState?.widgetType === WIDGET_TYPE.ORDER}
              onDefaultSymbol={(symbol, priceIncrement) => {
                setDefaultSymbol(symbol)
                if (!rowSelectedInstrumentSymbol) {
                  setSelectedInstrumentPriceIncrement(priceIncrement)
                }
              }}
              onFilterReset={() => {
                setRowSelectedInstrumentSymbol(undefined)
                setDefaultSymbol(undefined)
              }}
            />
          </div>
          {!isMobile && !isChartCollapsed && !isChartFullscreen && (
            <ChartResizeHandle
              value={chartHeightPx}
              min={TV_MIN_CHART_HEIGHT_PX}
              max={Number.isFinite(chartMaxPx) ? chartMaxPx : chartHeightPx + 1000}
              onChange={handleChartResize}
              onDragEnd={handleChartResizeEnd}
              aria-label={t('Markets.Resize chart')}
            />
          )}
          {!isMobile && selectedInstrumentSymbol && (
            <TradingViewChart
              timeframe={chartTimeframe}
              onTimeframeChange={(value) => {
                setChartTimeframe(value)
                writeStoredChartTimeframe(value)
              }}
              timezone={chartTimezone}
              onTimezoneChange={(value) => {
                setChartTimezone(value)
                writeStoredChartTimezone(value)
              }}
              apiClient={apiClient}
              symbol={selectedInstrumentSymbol}
              account={selectedAccount}
              collapsed={isChartCollapsed}
              isFullscreen={isChartFullscreen}
              isWidgetOpen={!!widgetState}
              height={chartHeightPx}
              onTradeClick={handleChartTradeClick}
              onToggleFullscreen={() => setIsChartFullscreen((prev) => !prev)}
              onSelectInstrument={(symbol) => {
                setRowSelectedInstrumentSymbol(symbol)
                handleInstrumentSelect(symbol)
              }}
              openPositions={openPositionsData.positions ?? []}
              priceIncrement={selectedInstrumentPriceIncrement}
            />
          )}
          {!isMobile && isChartCollapsed && <ChartToolbar onChartOpen={handleChartOpen} />}
        </div>

        {isMobile ? (
          widgetState &&
          selectedAccountLogin &&
          selectedInstrumentSymbol && (
            <WidgetContainer
              ref={widgetContainerRef}
              asset={widgetState.symbol}
              accountCode={selectedAccountLogin}
              initialSide={widgetState.side}
              initialTab={widgetState.tab}
              onClose={handleWidgetClose}
              onSideChange={handleSideChange}
              onTabChange={handleTabChange}
              onSwitchToOrderFromMarket={switchToOrderFromMarket}
              className={styles.orderWidgetWrapper}
              style={widgetContainerStyle}
              variant={
                widgetState.widgetType === WIDGET_TYPE.MARKET
                  ? WIDGET_TYPE.MARKET
                  : WIDGET_TYPE.ORDER
              }
              selectedAccount={selectedAccount}
              favoriteSymbols={favoriteSymbols}
              onToggleFavorite={handleToggleFavorite}
              api={apiClient}
              selectedInstrumentSymbol={selectedInstrumentSymbol}
            />
          )
        ) : (
          <AnimatePresence initial={false}>
            {widgetState && selectedAccountLogin && selectedInstrumentSymbol && (
              <WidgetContainer
                ref={widgetContainerRef}
                asset={widgetState.symbol}
                accountCode={selectedAccountLogin}
                initialSide={widgetState.side}
                initialTab={widgetState.tab}
                onClose={handleWidgetClose}
                onSideChange={handleSideChange}
                onTabChange={handleTabChange}
                onSwitchToOrderFromMarket={switchToOrderFromMarket}
                className={styles.orderWidgetWrapper}
                style={widgetContainerStyle}
                variant={
                  widgetState.widgetType === WIDGET_TYPE.MARKET
                    ? WIDGET_TYPE.MARKET
                    : WIDGET_TYPE.ORDER
                }
                selectedAccount={selectedAccount}
                favoriteSymbols={favoriteSymbols}
                onToggleFavorite={handleToggleFavorite}
                api={apiClient}
                selectedInstrumentSymbol={selectedInstrumentSymbol}
              />
            )}
          </AnimatePresence>
        )}
      </div>
    )
  }

  return null
}
